import React, { Component, ReactNode } from 'react';
import { Button, Result } from 'antd';
import i18n from '../i18n';

const t = (key: string) => i18n.t(key, { ns: 'pages' });

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  errorType: 'chunk' | 'generic' | null;
}

/**
 * Error Boundary that catches chunk loading errors
 * Shows user-friendly message with reload button
 */
class ChunkLoadErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      errorType: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Detect chunk loading errors
    const isChunkError =
      error.name === 'ChunkLoadError' ||
      error.message.includes('Loading chunk') ||
      error.message.includes('Failed to fetch dynamically imported module');

    return {
      hasError: true,
      errorType: isChunkError ? 'chunk' : 'generic',
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Note: No console.error per coding-style.md
    // Error is already in state, user sees UI
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError && this.state.errorType === 'chunk') {
      return (
        <Result
          status="warning"
          title={t('chunkError.loadTitle')}
          subTitle={t('chunkError.loadSubtitle')}
          extra={
            <Button type="primary" size="large" onClick={this.handleReload}>
              {t('chunkError.reload')}
            </Button>
          }
        />
      );
    }

    // For non-chunk errors, show generic error UI
    if (this.state.hasError) {
      return (
        <Result
          status="error"
          title={t('chunkError.genericTitle')}
          subTitle={t('chunkError.genericSubtitle')}
          extra={
            <Button type="primary" onClick={this.handleReload}>
              {t('chunkError.reload')}
            </Button>
          }
        />
      );
    }

    return this.props.children;
  }
}

export default ChunkLoadErrorBoundary;
